import React, { useEffect } from 'react';
import {
  Dialog,
  ListItemText,
  Divider,
  Box,
  MenuItem,
  ListItemIcon,
  MenuList,
} from '@mui/material';
import { useWhiteboardControl } from 'features/toolbar/toolbar/controls/whiteboard/useWhiteboardControl';
import { whiteboardViewed } from 'features/content-library/actions';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import Icon from 'components/Icon';
import {
  selectContentLibraryActiveWhiteboards,
  selectContentLibraryOpenedFile,
} from 'features/content-library/contentLibrarySlice';
import { selectIsLockedContentReadOnly } from 'features/layout/features/content/contentSlice';
import { closeModal, openModal } from 'features/modal/modalSlice';
import { ContentLockedAction } from 'features/layout/features/content/modals/ContentLockedByModeratorModal';
import { useBulkCloseScopeControl } from 'features/layout/grid-panel/useBulkCloseScopeControl';

const WhiteboardsModal = () => {
  const {
    isFeatureAvailable,
    createWhiteboard,
    label,
    publicLabel,
    privateLabel,
    icon,
    privateIcon,
    canManagePrivateWhiteboards,
  } = useWhiteboardControl();

  const {
    enabled: bulkCloseEnabled,
    handleClick: bulkClose,
    label: bulkCloseLabel,
  } = useBulkCloseScopeControl('whiteboards');

  const dispatch = useAppDispatch();
  const openedFile = useAppSelector(selectContentLibraryOpenedFile);
  const activeWhiteboards = useAppSelector(selectContentLibraryActiveWhiteboards);
  const isContentLockedForUser = useAppSelector(selectIsLockedContentReadOnly);

  const shouldClose = !isFeatureAvailable;

  useEffect(() => {
    if (shouldClose) {
      dispatch(closeModal('whiteboards'));
    }
  }, [dispatch, shouldClose]);

  const handleClose = () => {
    dispatch(closeModal('whiteboards'));
  };

  const handleCreateWhiteboard = (isPrivate: boolean) => {
    handleClose();

    if (isContentLockedForUser) {
      dispatch(
        openModal('contentLockedByModerator', {
          contentAction: ContentLockedAction.WhiteboardCreate,
        })
      );
      return;
    }

    createWhiteboard({ private: isPrivate });
  };

  const handleSelect = (index: number) => {
    handleClose();

    if (isContentLockedForUser) {
      dispatch(
        openModal('contentLockedByModerator', { contentAction: ContentLockedAction.WhiteboardOpen })
      );
      return;
    }

    dispatch(whiteboardViewed(activeWhiteboards[index].id));
  };

  return (
    <Dialog maxWidth="xs" onClose={handleClose} open={!shouldClose}>
      <MenuList>
        <Box sx={{ maxHeight: 'calc(min(200px, 30vh))', overflow: 'auto', maxWidth: '100%' }}>
          {activeWhiteboards.map((item, index) => (
            <MenuItem
              key={item.id}
              onClick={() => handleSelect(index)}
              sx={
                item.id === openedFile?.id
                  ? (theme) => ({
                      backgroundColor: theme.palette.primary.main,
                      color: 'white',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.main,
                      },
                    })
                  : null
              }
            >
              <ListItemText>{item.name}</ListItemText>
            </MenuItem>
          ))}
        </Box>
        {bulkCloseEnabled && (
          <>
            <Divider sx={{ mb: 1 }} />
            <MenuItem onClick={bulkClose}>
              <ListItemText>{bulkCloseLabel}</ListItemText>
            </MenuItem>
          </>
        )}
        <Divider hidden={activeWhiteboards.length === 0} sx={{ mb: 1 }} />
        <MenuItem onClick={() => handleCreateWhiteboard(false)}>
          <ListItemIcon sx={{ ml: -1 }}>
            <Icon name={icon} />
          </ListItemIcon>
          <ListItemText>{canManagePrivateWhiteboards ? publicLabel : label}</ListItemText>
        </MenuItem>
        {canManagePrivateWhiteboards && (
          <MenuItem onClick={() => handleCreateWhiteboard(true)}>
            <ListItemIcon sx={{ ml: -1 }}>
              <Icon name={privateIcon} />
            </ListItemIcon>
            <ListItemText>{privateLabel}</ListItemText>
          </MenuItem>
        )}
      </MenuList>
    </Dialog>
  );
};

export default WhiteboardsModal;
